import { type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { Icon } from '../icons';
import { formatShortcut, uiT, useUiT } from '../i18n';
import { copyDe, copyEn, registerDesignMessages } from '../i18n/design';
import { cx } from './cx';
import { Tooltip } from './Tooltip';
import { toast } from './Toast';
import '../styles/primitives/design.css';

registerDesignMessages(copyEn, copyDe);

/**
 * Copy text to the clipboard: the async Clipboard API, else a hidden selection with
 * `execCommand('copy')` (older browsers, plain-http on-prem hosts). Resolves whether it worked.
 */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Fall through to the selection fallback (permission denied, no focus).
  }
  if (typeof document === 'undefined') return false;
  const area = document.createElement('textarea');
  area.value = text;
  area.setAttribute('readonly', '');
  area.style.position = 'fixed';
  area.style.opacity = '0';
  document.body.appendChild(area);
  const active = document.activeElement as HTMLElement | null;
  area.select();
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch {
    ok = false;
  }
  area.remove();
  active?.focus?.();
  return ok;
}

export type CopyToastOptions = {
  /** What the toast says on success (default: "Copied to the clipboard"). */
  title?: string;
  body?: string;
  dedupeKey?: string;
};

/**
 * Copy text and say so: a toast on success, and on failure a hint to copy by hand. The one way for
 * "Copy link", "Copy ID" and "Copy code" actions without a button of their own (they have no
 * confirmation otherwise). Resolves whether it worked.
 */
export async function copyWithToast(text: string, opts: CopyToastOptions = {}): Promise<boolean> {
  const ok = await copyText(text);
  if (ok) {
    toast({
      title: opts.title ?? uiT('ui.copy.copied'),
      ...(opts.body ? { body: opts.body } : {}),
      tone: 'ok',
      durationMs: 2500,
      dedupeKey: opts.dedupeKey ?? 'ui.copy',
    });
  } else {
    toast({ title: uiT('ui.copy.failed', { shortcut: formatShortcut('mod+c') }), tone: 'warn' });
  }
  return ok;
}

export type CopyButtonProps = {
  value: string;
  /** What is copied ("Webhook URL"), for the button's accessible name. */
  label?: string;
  size?: 'sm' | 'md';
  /** Show a toast after copying (default true). */
  announce?: boolean;
  onCopy?: (value: string) => void;
  className?: string;
};

/** An icon button that copies `value` and confirms with a check mark and a toast. */
export function CopyButton({
  value,
  label,
  size = 'sm',
  announce = true,
  onCopy,
  className,
}: CopyButtonProps) {
  const t = useUiT();
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  const name = label ? t('ui.copy.copyNamed', { label }) : t('ui.copy.copy');
  const run = async () => {
    const ok = await copyText(value);
    if (ok) {
      setCopied(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 2000);
      if (announce) toast({ title: t('ui.copy.copied'), tone: 'ok', durationMs: 2500, dedupeKey: 'ui.copy' });
      onCopy?.(value);
    } else {
      toast({ title: t('ui.copy.failed', { shortcut: formatShortcut('mod+c') }), tone: 'warn' });
    }
  };
  return (
    <Tooltip content={copied ? t('ui.copy.copiedShort') : name}>
      <button
        type="button"
        className={cx('btn btn-g ib', size === 'sm' && 'sm', 'copy-btn', copied && 'copied', className)}
        aria-label={name}
        data-copied={copied || undefined}
        onClick={() => void run()}
      >
        <Icon name={copied ? 'check' : 'copy'} size={size === 'sm' ? 14 : 16} />
      </button>
    </Tooltip>
  );
}

export type CopyFieldProps = {
  value: string;
  label: ReactNode;
  /** Keep the label for screen readers only. */
  hideLabel?: boolean;
  hint?: ReactNode;
  /** Mask the value until the member reveals it (API keys, secrets). */
  secret?: boolean;
  mono?: boolean;
  size?: 'sm' | 'md';
  onCopy?: (value: string) => void;
  className?: string;
  'data-testid'?: string;
};

/**
 * A read-only value with a copy button (a share link, a webhook URL, an API key). Focusing the
 * value selects all of it, so ⌘C / Ctrl+C copies it too; the button confirms with a toast.
 */
export function CopyField({
  value,
  label,
  hideLabel,
  hint,
  secret,
  mono = true,
  size = 'md',
  onCopy,
  className,
  'data-testid': testId,
}: CopyFieldProps) {
  const t = useUiT();
  const id = useId();
  const [revealed, setRevealed] = useState(false);
  const masked = secret && !revealed;
  const labelText = typeof label === 'string' ? label : undefined;
  return (
    <div className={cx('copy-field', className)} data-testid={testId}>
      <label htmlFor={id} className={cx('copy-label', hideLabel && 'sr-only')}>
        {label}
      </label>
      <span className={cx('inp', size === 'sm' && 'sm', 'copy-frame')}>
        <input
          id={id}
          className={cx(mono && 'mono')}
          readOnly
          value={masked ? '•'.repeat(Math.min(24, Math.max(8, value.length))) : value}
          aria-describedby={hint ? `${id}-hint` : undefined}
          onFocus={(e) => e.currentTarget.select()}
          onCopy={(e) => {
            if (!masked) return;
            // A masked value copies the real secret, never the bullets.
            e.preventDefault();
            e.clipboardData.setData('text/plain', value);
          }}
          spellCheck={false}
        />
        {secret ? (
          <Tooltip content={revealed ? t('ui.copy.hide') : t('ui.copy.reveal')}>
            <button
              type="button"
              className={cx('btn btn-g ib sm')}
              aria-label={revealed ? t('ui.copy.hide') : t('ui.copy.reveal')}
              aria-pressed={revealed}
              aria-controls={id}
              onClick={() => setRevealed((r) => !r)}
            >
              <Icon name={revealed ? 'eye-off' : 'eye'} size={14} />
            </button>
          </Tooltip>
        ) : null}
        <CopyButton value={value} label={labelText} onCopy={onCopy} />
      </span>
      {hint ? (
        <span id={`${id}-hint`} className="copy-hint">
          {hint}
        </span>
      ) : null}
    </div>
  );
}
